import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  generateReport,
  generateReportFromAPI
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/service/api';
import { useAppSelector, useSnackbar } from 'src/utils/Hooks';
import { GenericErrorMessage } from 'src/components/BeaconV2Components/common/CommonSnackbarMessages/GenericErrorMessage';
import {
  PresentationForm,
  GenerateReportFromAPIOptions,
  GenerateReportFromAPIRequest
} from 'src/components/BeaconV2Components/ExperimentalLayout/Settings/Presentations/types';
import { useBeaconNavigator } from 'src/components/BeaconV2Components/BeaconNavigation/useBeaconNavigator';
import { DownloadsTab } from 'src/components/AtlasV2Components/AtlasV2Settings/AtlasV2DownloadsPageLayout';

const PRESENTATIONS_KEY = 'Presentations';

export const useGenerateReport = () => {
  const { beaconNavigator } = useBeaconNavigator();
  const queryClient = useQueryClient();
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const comparisonTimePeriod = useAppSelector((state) => state.comparisonTimePeriod);
  const categories = useAppSelector((state) => state.categories);
  const subCategories = useAppSelector((state) => state.subCategories);
  const { showSnackbar } = useSnackbar();

  const { mutate, isPending, mutateAsync, error, isSuccess } = useMutation({
    mutationFn: async (report: PresentationForm) => {
      const currentStartWeekId = mainTimePeriod?.startWeek;
      const currentEndWeekId = mainTimePeriod?.endWeek;
      const currentTimePeriodId = mainTimePeriod.id;
      const comparisonStartWeekId = comparisonTimePeriod?.startWeek;
      const comparisonEndWeekId = comparisonTimePeriod?.endWeek;
      const comparisonTimePeriodId = comparisonTimePeriod?.id;

      if (!report.categoryIds.length) {
        const subcatParents = subCategories
          .filter((subcat) => report.subcategoryIds.some((sc) => sc.id === subcat.id.toString()))
          .map((subcat) => subcat.parentCategoryId);
        const categoryIds = categories.filter((cat) => subcatParents.includes(cat.id));
        report.categoryIds = categoryIds.map((cat) => ({ id: cat.id.toString(), name: cat.categoryName }));
      }

      // Use different API based on whether the presentation is from API or hardcoded
      if (report.reportType.isFromAPI) {
        // Build options object conditionally - only include fields with values
        const options: GenerateReportFromAPIOptions = {
          startWeekId: currentStartWeekId,
          endWeekId: currentEndWeekId,
          comparisonPeriod: comparisonTimePeriodId,
          comparisonStartWeekId: comparisonStartWeekId,
          comparisonEndWeekId: comparisonEndWeekId,
          timePeriod: currentTimePeriodId,
          reportCurrency: report.currency
        };

        // Only add arrays if they have values
        if (report.categoryIds?.length) {
          options.categoryIds = report.categoryIds.map((c) => +c.id);
        }
        if (report.subcategoryIds?.length) {
          options.subcategoryIds = report.subcategoryIds.map((sc) => +sc.id);
        }
        if (report.retailerIds?.length) {
          options.retailerIds = report.retailerIds.map((r) => +r.id);
        }
        // brandIds - will be added later when functionality is implemented

        // Only add segment/businessUnit if applicable
        if (report.entityType.id === 'segment' && report.entityId?.id) {
          options.segmentId = report.entityId.id;
        }
        if (report.entityType.id === 'businessunit' && report.entityId?.id) {
          options.businessUnitId = report.entityId.id;
        }

        const apiRequestBody: GenerateReportFromAPIRequest = {
          presentationId: report.reportType.id,
          options
        };
        return await generateReportFromAPI(apiRequestBody);
      }

      const requestBody = {
        reportType: report.reportType.id,
        retailerIds: report.retailerIds.map((r) => +r.id),
        entityType: report.entityType.id,
        entityId: report.entityId.id,
        categoryIds: report.categoryIds.map((c) => +c.id),
        subcategoryIds: report.subcategoryIds.map((sc) => +sc.id),
        currentStartWeekId: currentStartWeekId,
        currentEndWeekId: currentEndWeekId,
        currentTimePeriod: currentTimePeriodId,
        comparisonStartWeekId: comparisonStartWeekId,
        comparisonEndWeekId: comparisonEndWeekId,
        comparisonTimePeriod: comparisonTimePeriodId,
        destinationCurrency: report.currency,
        modelName: 'OPENAI'
      };

      return await generateReport(requestBody);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [PRESENTATIONS_KEY]
      });
      showSnackbar({
        message: "You've successfully created a new report.",
        type: 'success'
      });
      beaconNavigator
        .setPathname('/account/downloads')
        .updateSearchParams({ tab: DownloadsTab.PresentationsDownload })
        .go();
    },
    onError: (error) => {
      let errorMessage: React.ReactNode = <GenericErrorMessage />;

      if (error && typeof error === 'object' && 'response' in error) {
        const response = (error as any).response;
        const errorCode = response?.data?.errorCode;
        if (errorCode === 'INSUFFICIENT_CREDITS') {
          errorMessage = 'You’ve used all available credits. Contact customer support to continue using Presentations.';
        } else if (errorCode === 'INACTIVE_CONTRACT') {
          errorMessage =
            'There are currently no active contracts. Contact customer support to continue using Presentations.';
        } else {
          errorMessage = 'Failed to generate report. Please try again.';
        }
      }

      showSnackbar({
        message: errorMessage,
        type: 'error'
      });
    }
  });

  return {
    generateReport: mutate,
    generateReportAsync: mutateAsync,
    isPending,
    error,
    isSuccess
  };
};
